import React, { useMemo } from 'react';
import styles from './DocumentInfo.module.scss';
import Card from '../Card/Card';
import { Form } from 'antd';
import DropdownSelect from '../../../../components/dropdownSelect';
import {
  allowedNextTypes,
  docTypes,
  sourceTypes,
} from '../../constants/documents.constants';
import { getRenderControl } from '../../../../components/SingleControlCustom/getRenderControl';
import { useSelector } from 'react-redux';
import { documentDrawerSelector } from '../../store/selectors/new-document.selector';
import { getInitialTypeId } from '../../utils/getInitialTypeId';

const mailTypes = [docTypes.incoming, docTypes.citizenAppeal];

const DocumentInfo = ({
  form,
  type,
  ordType,
  subtype,
  subtypesContract,
  subtypesOutgoing,
  subtypesIncoming,
  company,
}) => {
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form;

  const docType = getFieldValue('typeId');

  const { mode, savedDraft, formValues } = useSelector(documentDrawerSelector);

  const options = type.data || [];

  const isEdit = mode === 'edit';

  const isDisabled =
    (isEdit && formValues?.sourceId === sourceTypes.web) ||
    (isEdit && !formValues.canChange) ||
    type.isFetching;

  const serviceCompanyIdValue = getFieldValue('serviceCompanyId');

  const hasOneOptionsCompany = company.data?.length === 1;

  const isDisabledCompanySelect =
    company.isFetching || isEdit || hasOneOptionsCompany;

  const filterOptions = useMemo(() => {
    if (isEdit && formValues?.sourceId === sourceTypes.mail)
      return options.filter((option) => mailTypes.includes(option.value));

    if (!savedDraft) return options;

    const allowed = allowedNextTypes[savedDraft?.values?.type?.id] || [];

    return options.filter((option) => allowed.includes(option.value));
  }, [options, savedDraft]);

  return (
    <Card
      heading="Заполните информацию о документе"
      description="От выбранного типа зависят другие характеристики"
    >
      <div className={styles.root}>
        <Form.Item
          label="Управляющая компания"
          style={{ margin: 0 }}
        >
          {getFieldDecorator('serviceCompanyId', {
            rules: [
              {
                required: true,
                message: 'Пожалуйста, укажите управляющую компанию',
              },
            ],
          })(
            <DropdownSelect
              options={company.data}
              multiple={false}
              renderControl={getRenderControl({
                options: company.data,
                value: serviceCompanyIdValue,
                placeholder: 'Управляющая компания',
                disabled: isDisabledCompanySelect,
                loading: company.isFetching,
              })}
            />
          )}
        </Form.Item>

        <Form.Item
          style={{ margin: 0 }}
          label="Тип документа"
        >
          {getFieldDecorator('typeId', {
            initialValue: getInitialTypeId({ mode, formValues, savedDraft }),
            rules: [
              { required: true, message: 'Пожалуйста, укажите тип документа' },
            ],
          })(
            <DropdownSelect
              disabled={isDisabled}
              loading={type.isFetching}
              multiple={false}
              placeholder="Тип документа"
              options={filterOptions}
              renderControl={getRenderControl({
                options: filterOptions,
                isClear: false,
                value: getFieldValue('typeId'),
                disabled: isDisabled,
              })}
            />
          )}
        </Form.Item>
        <Form.Item style={{ margin: 0 }}>
          {docType === docTypes.organizational &&
            getFieldDecorator('ordTypeId', {
              initialValue: 1,
              rules: [
                {
                  required: true,
                  message: 'Пожалуйста, укажите тип ОРД',
                },
              ],
            })(
              <DropdownSelect
                multiple={false}
                placeholder="Тип ОРД"
                options={ordType.data}
                renderControl={getRenderControl({
                  options: ordType.data,
                  isClear: false,
                  value: getFieldValue('ordTypeId'),
                  disabled: ordType.isFetching || isEdit,
                  loading: ordType.isFetching,
                  placeholder: 'Тип ОРД',
                })}
              />
            )}
          {docType === docTypes.meeting &&
            getFieldDecorator('subTypeId', {
              initialValue: formValues?.subtype?.id || 11,
              rules: [
                {
                  required: true,
                  message: 'Пожалуйста, укажите тип ОСС',
                },
              ],
            })(
              <DropdownSelect
                multiple={false}
                placeholder="Тип ОСС"
                options={subtype.data}
                renderControl={getRenderControl({
                  options: subtype.data,
                  isClear: false,
                  value: getFieldValue('subTypeId'),
                  disabled: subtype.isFetching || isEdit,
                  loading: subtype.isFetching,
                  placeholder: 'Тип ОСС',
                })}
              />
            )}
          {docType === docTypes.contract &&
            getFieldDecorator('subTypeId', {
              initialValue: formValues?.subtype?.id || 4,
              rules: [
                {
                  required: true,
                  message: 'Пожалуйста, укажите тип договора',
                },
              ],
            })(
              <DropdownSelect
                multiple={false}
                placeholder="Тип договора"
                options={subtypesContract.data}
                renderControl={getRenderControl({
                  options: subtypesContract.data,
                  isClear: false,
                  value: getFieldValue('subTypeId'),
                  disabled: subtypesContract.isFetching || isEdit,
                  loading: subtypesContract.isFetching,
                  placeholder: 'Тип договора',
                })}
              />
            )}
          {docType === docTypes.incoming &&
            getFieldDecorator('subTypeId', {
              initialValue: formValues?.subtype?.id || null,
            })(
              <DropdownSelect
                multiple={false}
                placeholder="Вид входящего"
                options={subtypesIncoming.data}
                renderControl={getRenderControl({
                  options: subtypesIncoming.data,
                  isClear: isEdit ? false : true,
                  onClearClick: () => {
                    setFieldsValue({ subTypeId: null });
                  },
                  value: getFieldValue('subTypeId'),
                  disabled:
                    subtypesIncoming.isFetching ||
                    (isEdit && !formValues.canChange) ||
                    (isEdit && formValues?.sourceId === sourceTypes.web),
                  loading: subtypesIncoming.isFetching,
                  placeholder: 'Вид входящего',
                })}
              />
            )}
          {docType === docTypes.outgoing &&
            getFieldDecorator('subTypeId', {
              initialValue: formValues?.subtype?.id || null,
            })(
              <DropdownSelect
                multiple={false}
                placeholder="Вид исходящего"
                options={subtypesOutgoing.data}
                renderControl={getRenderControl({
                  options: subtypesOutgoing.data,
                  isClear: isEdit ? false : true,
                  onClearClick: () => {
                    setFieldsValue({ subTypeId: null });
                  },
                  value: getFieldValue('subTypeId'),
                  disabled: subtypesOutgoing.isFetching || isEdit,
                  loading: subtypesOutgoing.isFetching,
                  placeholder: 'Вид исходящего',
                })}
              />
            )}
        </Form.Item>
        {isEdit &&
          formValues?.sourceId === sourceTypes.mail &&
          formValues.canChange && (
            <div style={{ color: '#0995ED', fontSize: 14 }}>
              Обратите внимание: после сохранения документа изменить поля "Тип
              документа" и "Подтип документа" будет невозможно
            </div>
          )}
      </div>
    </Card>
  );
};

export default DocumentInfo;
